import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, MessageSquare, BookOpen, Target, Star, Calendar, Brain,
  ChevronRight, ChevronDown, Heart, Send, CheckCircle, Sparkles,
  ArrowRight, Play, Award, Zap, Users, Shield, Bell, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';

const YAS_LOGO = '/yas-logo.png';
const MOCKUP_DASHBOARD = 'https://static.prod-images.emergentagent.com/jobs/f301731c-0ed0-4765-951d-2e75dc724134/images/5beebc3c57c11d9d245ae0ae8540bea23ad064068ec388253b8aa81baff828ac.png';
const MOCKUP_WINWALL = 'https://static.prod-images.emergentagent.com/jobs/f301731c-0ed0-4765-951d-2e75dc724134/images/72aacbddd91ceaff0381779d5e3695a919122a326621a1fa6ebf83b72d211aed.png';
const MOCKUP_INBOX = 'https://static.prod-images.emergentagent.com/jobs/f301731c-0ed0-4765-951d-2e75dc724134/images/f8e668f1734e4ff8d1cfde9cd3e278083e8e04b1e0b52c53077a24262f517fb1.png';
const MOCKUP_WORKSHOP = 'https://static.prod-images.emergentagent.com/jobs/f301731c-0ed0-4765-951d-2e75dc724134/images/1c61727fb3e9344d1645a1433b14444942fe55ca12d404ed11d632d7e5fd8396.png';

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: Sparkles },
  { id: 'winwall', label: 'Win Wall', icon: Trophy },
  { id: 'courses', label: 'Courses', icon: BookOpen },
  { id: 'inbox', label: 'Inbox', icon: MessageSquare },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'goals', label: 'Goals', icon: Target },
  { id: 'ai', label: 'AI Guide', icon: Brain },
  { id: 'points', label: 'Points', icon: Star },
  { id: 'faq', label: 'FAQ', icon: Shield },
];

// Simulated Win Wall Post Component
const DemoWinPost = ({ name, text, likes, time, delay }) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(likes);
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay }}
      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white text-sm font-bold">{name.charAt(0)}</div>
        <div><p className="text-sm font-bold text-gray-900">{name}</p><p className="text-[10px] text-gray-400">{time}</p></div>
      </div>
      <p className="text-sm text-gray-700 mb-3">{text}</p>
      <div className="flex items-center gap-4">
        <button onClick={() => { setLiked(!liked); setLikeCount(liked ? likeCount - 1 : likeCount + 1); }}
          className={`flex items-center gap-1.5 text-xs font-bold transition-all ${liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}>
          <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} /> {likeCount}
        </button>
        <span className="flex items-center gap-1.5 text-xs text-gray-400"><MessageSquare className="w-4 h-4" /> Comment</span>
      </div>
    </motion.div>
  );
};

// Simulated Chat Message
const DemoChat = ({ messages }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  useEffect(() => {
    if (visibleCount < messages.length) {
      const timer = setTimeout(() => setVisibleCount(v => v + 1), 800);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, messages.length]);
  return (
    <div className="space-y-3 p-4 bg-gray-50 rounded-2xl max-h-64 overflow-hidden">
      {messages.slice(0, visibleCount).map((msg, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
          className={`flex ${msg.sent ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[75%] p-3 rounded-2xl text-sm ${msg.sent ? 'bg-orange-500 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'}`}>
            {msg.text}
          </div>
        </motion.div>
      ))}
      {visibleCount < messages.length && <div className="flex gap-1 justify-start pl-2"><div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" /><div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} /><div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} /></div>}
    </div>
  );
};

// FAQ Item
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50">
        <p className="text-sm font-bold text-gray-900 pr-4">{q}</p>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
          <p className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">{a}</p>
        </motion.div>}
      </AnimatePresence>
    </div>
  );
};

const YouthTutorialPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    }, { threshold: 0.3 });

    NAV_ITEMS.forEach(item => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Nav */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 py-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            <img src={YAS_LOGO} alt="YAS" className="w-8 h-8 flex-shrink-0" />
            {NAV_ITEMS.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 ${activeSection === item.id ? 'bg-orange-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ==================== HERO ==================== */}
      <section id="overview" className="relative overflow-hidden">
        <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 50%, #2D9EA3 100%)' }} className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.img initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 10 }}
              src={YAS_LOGO} alt="YAS Connect" className="w-20 h-20 mx-auto mb-6" />
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl font-black text-white mb-4">
              Your Summer.<br />Your Growth.<br /><span className="text-orange-400">Your Platform.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="text-lg text-white/70 max-w-md mx-auto mb-8">
              Everything you need for your program — courses, goals, your mentor, and your crew — all in one place.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
              className="flex gap-3 justify-center flex-wrap">
              <Button onClick={() => scrollTo('winwall')} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-5 text-base">
                See How It Works <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => navigate('/login')} variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 font-bold px-6 py-5 text-base">
                Sign In
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Quick Feature Cards */}
        <div className="max-w-4xl mx-auto px-4 -mt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Trophy, label: 'Win Wall', desc: 'Show off your wins', color: '#E86C3D' },
              { icon: BookOpen, label: 'Courses', desc: '13 interactive courses', color: '#8B5CF6' },
              { icon: MessageSquare, label: 'Inbox', desc: 'Message your mentor', color: '#10B981' },
              { icon: Star, label: 'Points', desc: 'Earn XP & badges', color: '#F59E0B' },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                onClick={() => scrollTo(f.label.toLowerCase().replace(' ', ''))}
                className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100 cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: `${f.color}15` }}>
                  <f.icon className="w-5 h-5" style={{ color: f.color }} />
                </div>
                <p className="text-sm font-bold text-gray-900">{f.label}</p>
                <p className="text-[10px] text-gray-500">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== WIN WALL ==================== */}
      <section id="winwall" className="py-16 px-4" style={{ backgroundColor: '#FFF7ED' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700 text-xs font-bold mb-3">
              <Trophy className="w-4 h-4" /> THE MAIN FEATURE
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-2">Win Wall</h2>
            <p className="text-gray-600 max-w-md mx-auto">Your crew is doing big things. Post your wins, hype each other up, and watch the energy grow.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div className="space-y-3">
              <DemoWinPost name="Marcus J." text="Just finished my first week at City Hall! The team is amazing and I already learned so much about how city government works." likes={12} time="2h ago" delay={0.1} />
              <DemoWinPost name="Aaliyah B." text="Completed the Money Moves workshop — I had no idea I was spending $200/month on DoorDash. Not anymore." likes={8} time="4h ago" delay={0.3} />
              <DemoWinPost name="DeShawn M." text="Got employee of the week at my worksite! My supervisor said my communication skills are next level." likes={15} time="Yesterday" delay={0.5} />
              <p className="text-center text-xs text-gray-400 italic">Try tapping the hearts above</p>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src={MOCKUP_WINWALL} alt="Win Wall" className="w-full max-w-xs mx-auto rounded-3xl shadow-2xl" />
            </motion.div>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md mx-auto">
            {[
              { val: '+50', label: 'pts per win', icon: Star },
              { val: 'Public', label: 'your cohort sees it', icon: Users },
              { val: 'Hype', label: 'like & comment', icon: Heart },
            ].map((s, i) => (
              <div key={i} className="text-center p-3 bg-white rounded-2xl shadow-sm">
                <s.icon className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                <p className="text-lg font-black text-gray-900">{s.val}</p>
                <p className="text-[9px] text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== WORKSHOPS ==================== */}
      <section id="courses" className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-xs font-bold mb-3">
              <BookOpen className="w-4 h-4" /> LEVEL UP
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-2">13 Interactive Courses</h2>
            <p className="text-gray-600 max-w-md mx-auto">Not boring lectures. Real activities you do on your phone — flip cards, quizzes, reflections. Earn XP and badges as you go.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src={MOCKUP_WORKSHOP} alt="Workshop" className="w-full max-w-xs mx-auto rounded-3xl shadow-2xl" />
            </motion.div>
            <div className="space-y-3">
              {[
                { title: 'Run Your Feels', desc: 'Emotional intelligence — know your triggers', color: '#E86C3D', modules: 7 },
                { title: 'Money Moves', desc: 'Financial literacy — stop the money leaks', color: '#10B981', modules: 5 },
                { title: 'Squad Up', desc: 'Teamwork — find your role on any team', color: '#3B82F6', modules: 5 },
                { title: 'Lead Mode', desc: 'Leadership — step up when it counts', color: '#EC4899', modules: 5 },
                { title: 'Stand Out', desc: 'Resume & interview — get the job', color: '#6366F1', modules: 5 },
              ].map((ws, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-black" style={{ backgroundColor: ws.color }}>
                    {ws.modules}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{ws.title}</p>
                    <p className="text-[10px] text-gray-500">{ws.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                </motion.div>
              ))}
              <p className="text-[10px] text-gray-400 text-center">+ 8 more courses</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== INBOX ==================== */}
      <section id="inbox" className="py-16 px-4" style={{ backgroundColor: '#F0FDF4' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold mb-3">
              <MessageSquare className="w-4 h-4" /> YOUR MENTOR
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-2">Direct Line to Your Mentor</h2>
            <p className="text-gray-600 max-w-md mx-auto">Message your program mentor directly. Ask questions, get advice, share updates. They get notified instantly.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <DemoChat messages={[
                { text: "Hey, quick question about tomorrow's schedule?", sent: true },
                { text: "Hey! The workshop starts at 9am in Room A. Bring your workbook!", sent: false },
                { text: "Got it, thanks! Also I finished the leadership course", sent: true },
                { text: "Amazing work! You're one of the top performers in your cohort", sent: false },
              ]} />
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="p-3 bg-white rounded-xl text-center shadow-sm">
                  <MessageSquare className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                  <p className="text-[10px] font-bold text-gray-700">Messages</p>
                </div>
                <div className="p-3 bg-white rounded-xl text-center shadow-sm">
                  <Shield className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-[10px] font-bold text-gray-700">Q&A</p>
                </div>
                <div className="p-3 bg-white rounded-xl text-center shadow-sm">
                  <BookOpen className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                  <p className="text-[10px] font-bold text-gray-700">FAQ</p>
                </div>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src={MOCKUP_INBOX} alt="Inbox" className="w-full max-w-xs mx-auto rounded-3xl shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== EVENTS ==================== */}
      <section id="events" className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-xs font-bold mb-3">
              <Calendar className="w-4 h-4" /> EVENTS
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-2">Your Schedule. Your Events.</h2>
            <p className="text-gray-600 max-w-md mx-auto">See your orientation schedule, check in to sessions, complete the workbook, and add events to your calendar.</p>
          </motion.div>
          <div className="max-w-md mx-auto space-y-3">
            <p className="text-xs text-gray-500 text-center italic mb-2">Example: How a summer program uses the Events feature</p>
            {[
              { day: 'Day 1', title: 'Welcome & Team Building Courses', sessions: 4, color: '#E86C3D' },
              { day: 'Day 2', title: 'Professional Skills & Leadership', sessions: 3, color: '#8B5CF6' },
              { day: 'Day 3', title: 'Financial Literacy & Career Planning', sessions: 3, color: '#10B981' },
              { day: 'Day 4', title: 'Presentations & Celebration', sessions: 2, color: '#F59E0B' },
            ].map((day, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xs font-black" style={{ backgroundColor: day.color }}>
                  {day.day.split(' ')[1]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-900">{day.title}</p>
                  <p className="text-[10px] text-gray-500">{day.sessions} sessions · Check in to earn points</p>
                </div>
                <CheckCircle className="w-5 h-5 text-gray-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== GOALS ==================== */}
      <section id="goals" className="py-16 px-4" style={{ backgroundColor: '#EFF6FF' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-xs font-bold mb-3">
              <Target className="w-4 h-4" /> GOALS
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-2">Set It. Track It. Crush It.</h2>
            <p className="text-gray-600 max-w-md mx-auto">Set SMART goals, track your progress weekly, and celebrate when you hit them. Your mentor can see your goals and help you stay on track.</p>
          </motion.div>
          <div className="max-w-md mx-auto space-y-3">
            {[
              { title: 'Complete 5 workshop courses', progress: 60, status: 'active' },
              { title: 'Save $500 from summer earnings', progress: 30, status: 'active' },
              { title: 'Get a reference letter from supervisor', progress: 100, status: 'completed' },
            ].map((goal, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className={`p-4 rounded-2xl ${goal.status === 'completed' ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-gray-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-gray-900">{goal.title}</p>
                  {goal.status === 'completed' ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <span className="text-xs font-bold text-orange-500">{goal.progress}%</span>}
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: `${goal.progress}%` }} viewport={{ once: true }} transition={{ delay: 0.5, duration: 1 }}
                    className={`h-full rounded-full ${goal.status === 'completed' ? 'bg-emerald-500' : 'bg-orange-500'}`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== AI GUIDE ==================== */}
      <section id="ai" className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-100 text-cyan-700 text-xs font-bold mb-3">
              <Brain className="w-4 h-4" /> AI POWERED
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-2">Your Personal AI Guide</h2>
            <p className="text-gray-600 max-w-md mx-auto">Get career suggestions based on YOUR interests and skills. The AI learns from your workshop responses, goals, and activity to give you personalized guidance.</p>
          </motion.div>
          <div className="max-w-md mx-auto">
            <Card className="p-5 rounded-2xl bg-gradient-to-br from-cyan-50 to-orange-50 border-cyan-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div><p className="text-sm font-bold text-gray-900">AI Career Guide</p><p className="text-[10px] text-gray-500">Personalized for you</p></div>
              </div>
              <div className="space-y-2">
                {['Based on your interest in technology and leadership, have you considered UX Design?', 'Your Money Moves workshop score was top 10% — you might enjoy a finance internship', 'You completed 3 communication courses — public speaking competitions could be your thing'].map((tip, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.2 }}
                    className="p-3 bg-white rounded-xl text-xs text-gray-700 border border-gray-100 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                    {tip}
                  </motion.div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* ==================== POINTS & BADGES ==================== */}
      <section id="points" className="py-16 px-4" style={{ backgroundColor: '#FFFBEB' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700 text-xs font-bold mb-3">
              <Star className="w-4 h-4" /> GAMIFICATION
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-2">Earn Points. Collect Badges. Compete.</h2>
            <p className="text-gray-600 max-w-md mx-auto">Everything you do earns points. Check the leaderboard to see where you rank in your cohort.</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-lg mx-auto">
            {[
              { action: 'Post a Win', pts: 50, icon: Trophy },
              { action: 'Complete Workshop', pts: 150, icon: BookOpen },
              { action: 'Weekly Check-in', pts: 20, icon: CheckCircle },
              { action: 'Set a Goal', pts: 25, icon: Target },
              { action: 'Finish Workbook', pts: 25, icon: Award },
              { action: 'Skills Assessment', pts: 50, icon: Zap },
              { action: 'Weekly Trivia', pts: 25, icon: Star },
              { action: 'Event Check-in', pts: 15, icon: Calendar },
            ].map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="p-3 bg-white rounded-2xl text-center shadow-sm border border-gray-100">
                <p.icon className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                <p className="text-lg font-black text-gray-900">+{p.pts}</p>
                <p className="text-[9px] text-gray-500">{p.action}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section id="faq" className="py-16 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-2">Got Questions?</h2>
          </motion.div>
          <div className="space-y-2">
            <FaqItem q="Do I need to download an app?" a="Nope! YAS Connect works in your phone's browser. But we recommend adding it to your home screen — you'll get notifications and it works like a real app. When you first log in, we'll show you how." />
            <FaqItem q="Can my mentor see everything I do?" a="Your mentor can see your goals, workshop progress, and wins you post. But your private reflections in courses are just between you and your AI Guide. Your mentor can't read your journal entries unless you share them." />
            <FaqItem q="How do I earn the most points?" a="Post wins (+50 each), complete courses (+150 each), and do your weekly check-in (+20). The top earners in each cohort get recognized on the leaderboard." />
            <FaqItem q="What if I have a problem at my worksite?" a="Message your mentor directly through the Inbox. They get an email notification and will respond. For urgent issues, call your program office." />
            <FaqItem q="Can I use this on my computer?" a="Yes! It works on any device — phone, tablet, or computer. Same login, same data everywhere." />
            <FaqItem q="Is my information private?" a="Yes. Your data is only visible to you, your mentor, and your program admin. We never share your information with other organizations or third parties." />
          </div>
        </div>
      </section>

      {/* ==================== CTA FOOTER ==================== */}
      <section className="py-16 px-4" style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 100%)' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black text-white mb-3">Ready to Start?</h2>
          <p className="text-white/60 mb-6">Log in, complete your agreements, and start exploring. Your summer starts now.</p>
          <Button onClick={() => navigate('/login')} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-5 text-lg">
            Sign In <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-white/30 text-xs mt-8">YAS Connect — Youth Achievement & Support</p>
        </div>
      </section>
    </div>
  );
};

export default YouthTutorialPage;
